import React, { useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { assetUrl, preloadPdfBrand } from "../lib/brand";
import { LayoutDashboard, Users, Briefcase, LibraryBig, Target, Boxes, LogOut, CreditCard, Activity, Mail, Settings, ShieldAlert, Gauge } from "lucide-react";

const navItems = [
  { to: "/app/executive", label: "Executive", icon: Gauge },
  { to: "/app", label: "Projects", icon: LayoutDashboard },
  { to: "/app/roles", label: "Roles", icon: Target },
  { to: "/app/teammates", label: "Teammates", icon: Users },
  { to: "/app/templates", label: "Templates", icon: LibraryBig },
  { to: "/app/risk-templates", label: "Risk Library", icon: ShieldAlert },
  { to: "/app/programs", label: "Programs", icon: Briefcase },
  { to: "/app/portfolios", label: "Portfolios", icon: Boxes },
  { to: "/app/resources", label: "Resources", icon: Activity },
  { to: "/app/invites", label: "Invites", icon: Mail },
  { to: "/app/settings", label: "Settings", icon: Settings },
];

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const orgBrand = user?.org_brand;
  useEffect(() => { preloadPdfBrand(orgBrand?.name ? orgBrand : null); }, [orgBrand]);

  return (
    <div className="min-h-screen">
      <header className="pc-glass-header sticky top-0 z-40">
        <div className="max-w-[1500px] mx-auto px-4 sm:px-6 py-3 flex items-center gap-6">
          <Link to="/app" className="flex items-center gap-2" data-testid="brand-link">
            <img src="/compass-logo.png" alt="Compass PM logo" className="w-9 h-9 object-contain" data-testid="brand-mark-pm" />
            <div className="flex flex-col leading-tight">
              <span className="font-bold text-white" style={{fontFamily:'Outfit'}}>Compass PM</span>
              <span className="text-[10px] uppercase tracking-widest text-blue-300/70">by Uplift Communications</span>
            </div>
          </Link>
          {orgBrand?.name && (
            <div className="hidden md:flex items-center gap-2 pl-4 border-l border-slate-700/70 min-w-0 max-w-[220px]" data-testid="header-org-brand">
              {orgBrand.logo_url && <img src={assetUrl(orgBrand.logo_url)} alt={`${orgBrand.name} logo`} className="h-8 w-auto max-w-[64px] object-contain rounded" data-testid="header-org-logo" onError={(e)=>{e.currentTarget.style.display='none';}} />}
              <div className="hidden xl:flex flex-col leading-tight min-w-0">
                <span className="text-sm font-semibold text-white truncate" data-testid="header-org-name">{orgBrand.name}</span>
                {orgBrand.division && <span className="text-[10px] uppercase tracking-widest text-slate-400 truncate" data-testid="header-org-division">{orgBrand.division}</span>}
              </div>
            </div>
          )}

          <nav className="hidden md:flex items-center gap-1 ml-4" data-testid="main-nav">
            {navItems.map((n) => {
              const active = loc.pathname === n.to || (n.to !== "/app" && loc.pathname.startsWith(n.to));
              const Icon = n.icon;
              return (
                <Link key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase()}`}
                  className={`px-3 py-2 rounded-lg text-sm flex items-center gap-2 transition-colors ${active ? "bg-blue-600/20 text-blue-300" : "text-slate-300 hover:text-white hover:bg-slate-800/60"}`}>
                  <Icon size={16} /> {n.label}
                </Link>
              );
            })}
          </nav>

          <div className="ml-auto flex items-center gap-3">
            {user?.subscription_status !== "active" && (
              <button data-testid="upgrade-btn" onClick={() => nav("/pricing")}
                className="hidden sm:inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm bg-gradient-to-r from-blue-500 to-blue-700 text-white font-medium shadow-lg shadow-blue-900/30 hover:from-blue-400 hover:to-blue-600">
                <CreditCard size={14}/> Upgrade
              </button>
            )}
            <div className="text-right hidden sm:block">
              <div className="text-sm text-white font-medium" data-testid="user-name">{user?.name}</div>
              <div className="text-xs text-slate-400">{user?.email}</div>
            </div>
            <button data-testid="logout-btn" onClick={logout}
              className="p-2 rounded-lg text-slate-300 hover:bg-slate-800 hover:text-white" title="Log out">
              <LogOut size={16}/>
            </button>
          </div>
        </div>

        <nav className="md:hidden px-4 pb-3 flex gap-2 overflow-x-auto" data-testid="mobile-nav">
          {navItems.map((n) => {
            const active = loc.pathname === n.to || (n.to !== "/app" && loc.pathname.startsWith(n.to));
            return (
              <Link key={n.to} to={n.to} data-testid={`mobile-nav-${n.label.toLowerCase()}`} className={`whitespace-nowrap px-3 py-1.5 rounded-lg text-xs ${active ? "bg-blue-600/25 text-blue-300" : "text-slate-300 bg-slate-800/50"}`}>
                {n.label}
              </Link>
            );
          })}
        </nav>
      </header>

      <main className="max-w-[1500px] mx-auto px-4 sm:px-6 py-8">
        {children}
      </main>
    </div>
  );
}
